import * as Keychain from 'utils/packages/keychain';
import FingerprintScanner from 'utils/packages/fingerprintScanner';
import { useCallback } from 'react';
import { Platform } from 'react-native';
import PasscodeModal from 'design-system/PasscodeModal';
import { KEYCHAIN_SERVICE, KEYCHAIN_SERVICE_RAW } from 'utils/passcode';
import { selectBiometricsEnabled, selectUserId } from 'reducers/selectors';
import { useAppStore } from 'store/hooks';
import usePasscodeVerifier from './usePasscodeVerifier';
/**
 * Can be used to get a passcode either using PasscodeModal or directly from the keychain using biometrics
 * In reality we may want to further encapsulate
 * @returns The users passcode, ready to be sent to the api
 */
const usePasscodeModal = () => {
    const [, isOutdated] = usePasscodeVerifier();
    const store = useAppStore();
    const authenticate = useCallback(async (params = {
        biometryDescription: 'Confirm using biometrics',
        title: 'Confirm using your passcode',
        forcePinAuthentication: false,
    }) => {
        const state = store.getState();
        const userId = selectUserId(state);
        const isBiometricsEnabled = selectBiometricsEnabled(state);
        const { forcePinAuthentication } = params;
        let { biometryDescription, title } = params;
        title = title || 'Confirm using your passcode';
        biometryDescription = title || 'Confirm using biometrics';
        if (!isOutdated && !forcePinAuthentication && isBiometricsEnabled) {
            try {
                const result = await Keychain.getGenericPassword({
                    service: KEYCHAIN_SERVICE,
                    authenticationPrompt: { title: biometryDescription },
                });
                if (typeof result !== 'boolean' &&
                    result.username === userId.toFixed(0)) {
                    // Successfully retrieved PIN using biometrics
                    return result.password;
                }
            }
            catch {
                // if we couldn’t retrive that’s fine we can get the user to manually enter
            }
            // On android, the pin can also be stored raw (unhashed) in keystore.
            // If that’s so, show face/touch prompt and return stored pin
            if (Platform.OS === 'android') {
                try {
                    const result = await Keychain.getGenericPassword({
                        service: KEYCHAIN_SERVICE_RAW,
                    });
                    if (typeof result !== 'boolean' &&
                        result.username === userId.toFixed(0)) {
                        // Successfully retrieved PIN. Now show face/touch prompt
                        try {
                            await FingerprintScanner.authenticate({
                                description: biometryDescription,
                            });
                            FingerprintScanner.release();
                            return result.password;
                        }
                        catch {
                            // fallback to manual pin entry
                        }
                    }
                }
                catch {
                    // fallback to manual pin entry
                }
            }
        }
        return new Promise((resolve, reject) => {
            PasscodeModal.show({
                title,
                onSuccess: (passcode) => {
                    resolve(passcode);
                },
                onFailure: () => {
                    reject();
                },
            });
        });
    }, [isOutdated]);
    return [authenticate];
};
export default usePasscodeModal;
